import { useCallback, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { FilterSlideout, createChipSelectSection, createSingleSelectSection } from '@aimsos/ui';

import { SORT_DIRECTION_OPTIONS } from '../constants/dataListFilters.js';

/**
 * The "All filters" panel.
 *
 * It is not a nicety. `Filters` collapses below 1280px to search plus this
 * button — the chips and the sort control are desktop-only — so without the
 * panel a laptop gets a search box and nothing else. The Widget Library has the
 * same shape for the same reason.
 *
 * One section per dimension the payload sent, so a tenant adding a filter in
 * Data Studio gets it here with no release. Section titles are the tenant's own
 * field names and are NOT translated; only the panel's chrome is.
 *
 * Sort lives here too, which the Widget Library's panel does not do. Its bar
 * hides the sort control below 1280px as well, and a list you cannot reorder on
 * a laptop is a gap rather than a decision.
 *
 * **Staged**: sections write to `draft`, and only `Apply` commits it, so
 * dismissing the panel leaves the list untouched.
 *
 * `showSort={false}` drops the sort sections for a list that has no order to
 * pick (Tasks, CT-5722, is grouped by urgency).
 */
function DataListFilterSlideout({ isOpen, onClose, filtersState, filterDefs, sortDefs, showSort = true }) {
  const { t } = useTranslation('unified-profiles');
  const {
    draft,
    setDraftFilter,
    applyDraft,
    clearDraft,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
  } = filtersState;

  const sections = useMemo(() => {
    const filterSections = filterDefs.map((def) =>
      createChipSelectSection({
        id: def.id,
        title: def.label,
        multiple: true,
        options: def.options,
        value: draft[def.id] ?? [],
        onChange: (next) => setDraftFilter(def.id, Array.isArray(next) ? next : [next].filter(Boolean)),
      }),
    );

    // Sort commits immediately rather than staging: it is not a filter, and
    // holding it behind Apply would make the panel's two halves behave
    // differently for no reason the user can see.
    const sortSections =
      sortDefs.length > 1
        ? [
            createSingleSelectSection({
              id: 'sort-field',
              title: t('list.sort.label'),
              options: sortDefs,
              value: sortField,
              onChange: setSortField,
            }),
          ]
        : [];

    if (!showSort) return filterSections;

    return [
      ...filterSections,
      ...sortSections,
      createSingleSelectSection({
        id: 'sort-direction',
        title: t('list.sort.directionLabel'),
        options: SORT_DIRECTION_OPTIONS.map(({ value, labelKey }) => ({
          value,
          label: t(labelKey),
        })),
        value: sortDirection,
        onChange: setSortDirection,
      }),
    ];
  }, [filterDefs, sortDefs, showSort, draft, setDraftFilter, sortField, setSortField, sortDirection, setSortDirection, t]);

  const handleApply = useCallback(() => {
    applyDraft();
    onClose?.();
  }, [applyDraft, onClose]);

  return (
    <FilterSlideout
      isOpen={isOpen}
      onClose={onClose}
      title={t('list.filters.allFilters')}
      sections={sections}
      onClearAll={clearDraft}
      onApply={handleApply}
    />
  );
}

export default DataListFilterSlideout;
